const ONBOARDING_TOKEN_KEY = "onboardingJwt";
const JWT_COOKIE_NAME = "jwtToken";

export const setOnboardingToken = (token: string): void => {
  if (token.trim()) {
    sessionStorage.setItem(ONBOARDING_TOKEN_KEY, token.trim());
  }
};

export const getOnboardingToken = (): string | null => {
  try {
    const token = sessionStorage.getItem(ONBOARDING_TOKEN_KEY);
    return token?.trim() ? token : null;
  } catch {
    return null;
  }
};

export const clearOnboardingToken = (): void => {
  try {
    sessionStorage.removeItem(ONBOARDING_TOKEN_KEY);
  } catch {
    // ignore sessionStorage errors
  }
};

const readJwtCookie = (): string | undefined => {
  if (typeof document === "undefined") return undefined;
  const prefix = `${JWT_COOKIE_NAME}=`;
  for (const part of document.cookie.split(";")) {
    const trimmed = part.trim();
    if (trimmed.startsWith(prefix)) {
      return decodeURIComponent(trimmed.slice(prefix.length));
    }
  }
  return undefined;
};

const JWT_PATTERN = /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/;

const findJwtInValue = (value: unknown, depth = 0): string | undefined => {
  if (depth > 6) return undefined;
  if (typeof value === "string") {
    const trimmed = value.trim();
    return JWT_PATTERN.test(trimmed) ? trimmed : undefined;
  }
  if (!value || typeof value !== "object") return undefined;
  for (const nested of Object.values(value as Record<string, unknown>)) {
    const jwt = findJwtInValue(nested, depth + 1);
    if (jwt) return jwt;
  }
  return undefined;
};

/** Reads token from login / authstatus / updatetoken response bodies. */
export const extractOnboardingToken = (json: unknown): string | undefined => {
  if (!json || typeof json !== "object") return undefined;
  const root = json as Record<string, unknown>;
  const nested =
    (root.data && typeof root.data === "object"
      ? (root.data as Record<string, unknown>)
      : undefined) ??
    (root.result && typeof root.result === "object"
      ? (root.result as Record<string, unknown>)
      : undefined) ??
    root;

  for (const value of [
    nested.jwtToken,
    nested.token,
    nested.jwt,
    nested.accessToken,
    nested.access_token,
    root.jwtToken,
    root.token,
    root.jwt,
  ]) {
    if (typeof value === "string" && value.trim()) {
      return value.trim();
    }
  }

  return findJwtInValue(json);
};

/** sessionStorage → readable jwtToken cookie (non-httpOnly). */
export const getOnboardingJwtForAuth = (): string | undefined => {
  const cached = getOnboardingToken();
  if (cached) return cached;

  const fromCookie = readJwtCookie();
  if (fromCookie) {
    setOnboardingToken(fromCookie);
    return fromCookie;
  }

  return undefined;
};

const extractTokenFromResponse = async (
  response: unknown,
): Promise<string | undefined> => {
  if (!(response instanceof Response) || !response.ok) return undefined;
  try {
    const json = await response.json();
    const jwt = extractOnboardingToken(json);
    if (jwt) {
      setOnboardingToken(jwt);
      return jwt;
    }
  } catch {
    // ignore parse errors
  }
  return undefined;
};

/** Resolves onboarding JWT for OnBoarding API `token` header (ImportExcelData, etc.). */
export const resolveOnboardingJwtForAuth = async (
  fetchAuthStatus: () => Promise<Response | unknown>,
  fetchUpdateToken?: () => Promise<Response | unknown>,
): Promise<string | undefined> => {
  const existing = getOnboardingJwtForAuth();
  if (existing) return existing;

  if (fetchUpdateToken) {
    const fromUpdateToken = await extractTokenFromResponse(
      await fetchUpdateToken(),
    );
    if (fromUpdateToken) return fromUpdateToken;
  }

  return extractTokenFromResponse(await fetchAuthStatus());
};
